import React, { useEffect, useState, useRef } from "react";
import { EnlargedImageOverlay } from "./EnlargedImageOverlay";
import "./NASAImageGallery.css";

const IMAGES_PER_PAGE = 35;

export const NASAImageGallery = ({ onClose, category = "iss" }) => {
  const [allImageData, setAllImageData] = useState([]); // Only metadata
  const [cachedPages, setCachedPages] = useState({}); // Cached slices
  const [currentImages, setCurrentImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enlargedIndex, setEnlargedIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const popupRef = useRef(null);

  // Fetch metadata (URLs & titles) once
  useEffect(() => {
    let isCancelled = false;
    async function loadMetadata() {
      setLoading(true);
      setError(null);
      setCurrentPage(1);
      setCachedPages({});
      setCurrentImages([]);

      try {
        const res = await fetch(`/data/nasa_images_${category}.json`);
        if (!res.ok) throw new Error(`Failed to load JSON: ${res.statusText}`);

        const data = await res.json();
        if (!isCancelled) {
          setAllImageData(data.data || []);
          setLoading(false);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error("Error loading metadata:", err);
          setError(err.message);
          setLoading(false);
        }
      }
    }

    loadMetadata();
    return () => {
      isCancelled = true;
    };
  }, [category]);

  const totalPages = Math.ceil(allImageData.length / IMAGES_PER_PAGE);

  // Load images for current page
  useEffect(() => {
    if (!allImageData.length) return;

    const loadPage = async (page) => {
      // Already cached?
      if (cachedPages[page]) {
        setCurrentImages(cachedPages[page]);
        return;
      }

      const start = (page - 1) * IMAGES_PER_PAGE;
      const end = start + IMAGES_PER_PAGE;
      const pageImages = allImageData.slice(start, end);

      setCachedPages((prev) => ({ ...prev, [page]: pageImages }));
      setCurrentImages(pageImages);
    };

    loadPage(currentPage);
  }, [currentPage, allImageData]);

  useEffect(() => {
    if (popupRef.current) {
      popupRef.current.scrollTop = 0;
    }
  }, [currentPage]);

  if (loading) {
    return (
      <div style={loadingStyle}>Loading images...</div>
    );
  }

  if (error) return <p style={{ color: "red" }}>Error: {error}</p>;



  return (
    <div 
      className="nasa-gallery-popup"
      style={popupStyle}
      {...(enlargedIndex === null && {
        onWheelCapture: (e) => e.stopPropagation(),
        onPointerDownCapture: (e) => e.stopPropagation(),
        onTouchStartCapture: (e) => e.stopPropagation(),
      })}
    > 
      <button onClick={onClose} style={closeButtonStyle}>×</button>

      <div style={{ paddingBottom: "1.5rem" }}>
        <h3>NASA {category.toUpperCase()} Gallery</h3>
        <div style={{fontSize: '0.75rem'}}>Page {currentPage} of {totalPages}</div>
      </div>

      <div 
        className="nasa-gallery-container"
        ref={popupRef}
      >
        {currentImages.map((img, index) => (
          <div
            key={(currentPage - 1) * IMAGES_PER_PAGE + index}
            className="nasa-gallery-item"
            // onClick={() => setEnlargedIndex((currentPage - 1) * IMAGES_PER_PAGE + index)}
            onClick={() => setEnlargedIndex(index)}
          >
            <img src={img.url} alt={img.title} loading="lazy" />
            <p>{img.title}</p>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div style={{ textAlign: "center", marginTop: "1rem" }}>
        {/* First */}
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          style={paginationButtonStyle(currentPage === 1)}
        >
          ⏮
        </button>

        {/* Previous */}
        <button
          onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
          disabled={currentPage === 1}
          style={paginationButtonStyle(currentPage === 1)}
        >
          ◀
        </button>

        {/* Dynamic page numbers */}
        {Array.from({ length: 4 }, (_, i) => {
          let page = currentPage - 2 + i;
          if (currentPage <= 2) page = i + 1;
          if (currentPage >= totalPages - 1) page = totalPages - 3 + i;
          if (page < 1 || page > totalPages) return null;

          return (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              style={{
                margin: "0 4px",
                padding: "0.3rem 0.3rem",
                fontWeight: page === currentPage ? "bold" : "normal",
                backgroundColor: page === currentPage ? "#fff" : "#333",
                color: page === currentPage ? "#000" : "#fff",
                border: "1px solid #888",
                borderRadius: "4px",
                cursor: page === currentPage ? "default" : "pointer",
                pointerEvents: page === currentPage ? "none" : "auto",
              }}
            >
              {page}
            </button>
          );
        })}

        {/* Next */}
        <button
          onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
          disabled={currentPage === totalPages}
          style={paginationButtonStyle(currentPage === totalPages)}
        >
          ▶
        </button>

        {/* Last */}
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          style={paginationButtonStyle(currentPage === totalPages)}
        >
          ⏭
        </button>
      </div>

      {/* Enlarged Image Overlay */}
      {enlargedIndex !== null && (
        <EnlargedImageOverlay
        images={currentImages.map((img) => img.url)}
          captions={currentImages.map((img) => {
            const year = img.date_created ? new Date(img.date_created).getFullYear() : "n.d.";
            const description = img.description || "No description provided.";
            const title = img.title || "Untitled";

            return (
              <>
                <strong>{title}:</strong> {description} [{year}]              
                <small style={{ fontSize: "0.6rem", display: "block", marginTop: "0.5rem" }}>
                  {img.photographer || "NASA"}. ({year}). <em>{title}</em> [Photograph]. {img.center || "NASA"}.<br />
                  <div  style={{ color: "#99ccff", wordBreak: "break-all", fontSize: "0.6rem" }}>{img.url}</div>
                </small>
              </>
            );
          })}
          initialIndex={enlargedIndex}
          totalCount={allImageData.length}
          globalIndexOffset={(currentPage - 1) * IMAGES_PER_PAGE}
          onClose={() => setEnlargedIndex(null)}
        />
      )}
    </div>
  );
};

// Reusable styles
const popupStyle = {
  zIndex: 100000,
  position: "fixed",
  top: "15vh",
  left: "50%",
  transform: "translateX(-50%)",
  width: "90vw",
  maxHeight: "80vh",
  overflowY: "auto",
  padding: "1rem",
  backgroundColor: "#1a1a1aa2",
  color: "white",
  borderRadius: "8px",
  pointerEvents: "auto",
  backdropFilter: "blur(8px)",
};

const loadingStyle = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  color: "white",
  backgroundColor: "rgba(0, 0, 0, 0.8)",
  padding: "1rem 2rem",
  borderRadius: "8px",
  zIndex: 99999,
  fontSize: "1.2rem",
};

const closeButtonStyle = {
  position: "absolute",
  top: "1rem",
  right: "1rem",
  background: "rgba(0,0,0,0.7)",
  color: "white",
  border: "none",
  padding: "0.5rem 1rem",
  fontSize: "1rem",
  borderRadius: "5px",
  cursor: "pointer",
  zIndex: 10001,
};

const paginationButtonStyle = (disabled) => ({
  margin: "0 4px",
  padding: "0.3rem 0.4rem",
  fontWeight: "normal",
  backgroundColor: "#333",
  color: "#fff",
  border: "0.8px solid #888",
  borderRadius: "4px",
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? "default" : "pointer",
});
